import {
  ACCESSORY_IMG,
  BACKDROP_BG,
  BACKDROP_PALETTE,
  BACKDROP_WAVE,
  EFFECT_IMG,
  HACKPAL_IMG,
  HEADER_TEXT_GRADIENT,
  HeaderGradientStop,
  INK_STAMP_IMG,
  PAPER_COLOR,
  PAPER_TEXTURE,
  SIGN_TEXT_GRADIENT,
} from "./assetMap";
import { HEADER_TEXT_LINES, resolveSignText } from "./constants";
import {
  fillFontCharCentered,
  fillFontTextTop,
  getHeadingFont,
  getInkFont,
  loadGraphicFonts,
  measureFontText,
} from "./graphicFonts";
import {
  INK_NAME_ARC_RADIUS_FRAC,
  INK_NAME_ARC_SPAN,
  INK_NAME_LETTER_SPACING_EM,
  inkNameArcCenterYFrac,
  inkNameFontSizePx,
} from "./InkStampLayer";
import { LAYOUT, OrientationLayout, pct, TextSpec } from "./layout";
import { resolveSignTextLayout } from "./signLayout";
import { GraphicCustomization } from "./types";

/**
 * Shared canvas renderer for the live preview and PNG export.
 * Text is drawn from OpenType glyph paths (not fillText) so Safari and
 * Chrome produce the same metrics and positions.
 */

const imageCache = new Map<string, Promise<HTMLImageElement>>();

const loadImage = (src: string): Promise<HTMLImageElement> => {
  let pending = imageCache.get(src);
  if (!pending) {
    pending = (async () => {
      const img = new Image();
      img.src = src;
      await img.decode();
      return img;
    })();
    imageCache.set(src, pending);
  }
  return pending;
};

const wrapText = (
  text: string,
  maxWidth: number,
  maxLines: number,
  fontSize: number
): string[] => {
  const font = getHeadingFont();
  const words = text.split(" ");
  const lines: string[] = [];
  let current = "";
  words.forEach((word) => {
    const candidate = current ? `${current} ${word}` : word;
    if (measureFontText(font, candidate, fontSize) <= maxWidth || !current) {
      current = candidate;
    } else {
      lines.push(current);
      current = word;
    }
  });
  if (current) lines.push(current);
  return lines.slice(0, maxLines);
};

const fillStyleForLine = (
  ctx: CanvasRenderingContext2D,
  colorOrGradient: string | HeaderGradientStop[],
  lineX: number,
  lineWidth: number
): string | CanvasGradient => {
  if (typeof colorOrGradient === "string") return colorOrGradient;
  const gradient = ctx.createLinearGradient(lineX, 0, lineX + lineWidth, 0);
  colorOrGradient.forEach((stop) => {
    gradient.addColorStop(stop.offset, stop.color);
  });
  return gradient;
};

const drawText = (
  ctx: CanvasRenderingContext2D,
  textLines: string[],
  spec: TextSpec,
  colorOrGradient: string | HeaderGradientStop[],
  stampW: number,
  stampH: number
) => {
  const font = getHeadingFont();
  const fontSize = pct(spec.fontPct, stampW);
  const maxWidth = pct(spec.maxW, stampW);
  const lines =
    spec.maxLines === 1
      ? [textLines.join(" ")]
      : textLines.length > 1
      ? textLines.slice(0, spec.maxLines)
      : wrapText(textLines[0], maxWidth, spec.maxLines, fontSize);
  const x = pct(spec.x, stampW);
  const y = pct(spec.y, stampH);

  ctx.save();
  if (spec.rotationDeg !== 0) {
    ctx.translate(x, y);
    ctx.rotate((spec.rotationDeg * Math.PI) / 180);
  }

  lines.forEach((line, i) => {
    const drawX = spec.rotationDeg !== 0 ? 0 : x;
    const drawY =
      spec.rotationDeg !== 0
        ? i * fontSize * spec.lineHeight
        : y + i * fontSize * spec.lineHeight;
    const measured = measureFontText(font, line, fontSize);
    const lineLeft = spec.align === "center" ? drawX - measured / 2 : drawX;
    const fillStyle = fillStyleForLine(
      ctx,
      colorOrGradient,
      lineLeft,
      measured
    );
    fillFontTextTop(
      ctx,
      font,
      line,
      drawX,
      drawY,
      fontSize,
      spec.align,
      fillStyle
    );
  });
  ctx.restore();
};

const drawCircularName = (
  ctx: CanvasRenderingContext2D,
  name: string,
  diameter: number,
  color: string,
  nameYOffsetPct: number
) => {
  const displayName = name.trim().toUpperCase();
  if (!displayName) return;

  const font = getInkFont();
  const fontSize = inkNameFontSizePx(displayName, diameter);
  const radius = diameter * INK_NAME_ARC_RADIUS_FRAC;
  const arcCenterY = diameter * inkNameArcCenterYFrac(nameYOffsetPct);
  const letterSpacing = fontSize * INK_NAME_LETTER_SPACING_EM;

  ctx.save();
  ctx.translate(0, arcCenterY);

  const chars = displayName.split("");
  const widths = chars.map((c) => measureFontText(font, c, fontSize));
  const gaps = Math.max(chars.length - 1, 0) * letterSpacing;
  const totalWidth = widths.reduce((sum, w) => sum + w, 0) + gaps;
  const arcLen = Math.min(totalWidth, radius * INK_NAME_ARC_SPAN);
  const scale = totalWidth > 0 ? arcLen / totalWidth : 1;
  let angle = -arcLen / 2 / radius;

  chars.forEach((char, i) => {
    const charW = widths[i] * scale;
    const gap = i < chars.length - 1 ? letterSpacing * scale : 0;
    const mid = angle + charW / 2 / radius;
    ctx.save();
    ctx.rotate(mid);
    // -1px along the outward radial = slightly toward the top of the stamp
    ctx.translate(0, -radius - 11.5);
    fillFontCharCentered(ctx, font, char, fontSize, color);
    ctx.restore();
    angle += (charW + gap) / radius;
  });
  ctx.restore();
};

const drawInkStamp = (
  ctx: CanvasRenderingContext2D,
  stampImg: HTMLImageElement,
  firstName: string,
  color: string,
  layout: OrientationLayout
) => {
  const { canvas, inkStamp } = layout;
  const cx = pct(inkStamp.cx, canvas.width);
  const cy = pct(inkStamp.cy, canvas.height);
  const d = pct(inkStamp.d, canvas.width);

  ctx.save();
  ctx.translate(cx, cy);
  ctx.rotate((inkStamp.rotationDeg * Math.PI) / 180);
  ctx.drawImage(stampImg, -d / 2, -d / 2, d, d);
  drawCircularName(ctx, firstName, d, color, inkStamp.nameYOffsetPct);
  ctx.restore();
};

/** Paint the graphic onto a new canvas at native export resolution. */
export const renderGraphicToCanvas = async (
  customization: GraphicCustomization,
  _firstName: string
): Promise<HTMLCanvasElement> => {
  const { orientation, backdrop, hackpal, feeling, accessory, effect } =
    customization;
  const layout = LAYOUT[orientation];
  const palette = BACKDROP_PALETTE[backdrop];
  const signText = resolveSignText(customization);
  // Use the field as-is (including empty) — do not fall back to profile name.
  const stampName = customization.stampName;

  const canvas = document.createElement("canvas");
  canvas.width = layout.canvas.width;
  canvas.height = layout.canvas.height;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas 2D context unavailable");

  await loadGraphicFonts();

  const accessorySrc = ACCESSORY_IMG[accessory];
  const effectSrc = EFFECT_IMG[effect];
  const [bgImg, palImg, paperImg, waveImg, inkStampImg] = await Promise.all([
    loadImage(BACKDROP_BG[backdrop][orientation]),
    loadImage(HACKPAL_IMG[hackpal][feeling]),
    loadImage(PAPER_TEXTURE),
    loadImage(BACKDROP_WAVE[backdrop]),
    loadImage(INK_STAMP_IMG[backdrop]),
  ]);
  const accessoryImg = accessorySrc ? await loadImage(accessorySrc) : null;
  const effectImg = effectSrc ? await loadImage(effectSrc) : null;

  ctx.fillStyle = PAPER_COLOR;
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(paperImg, 0, 0, canvas.width, canvas.height);

  const stampW = pct(layout.stamp.w, canvas.width);
  const stampH = (stampW / canvas.width) * canvas.height;
  const stampX = pct(layout.stamp.x, canvas.width);
  const stampY = pct(layout.stamp.y, canvas.height);

  ctx.save();
  ctx.translate(stampX + stampW / 2, stampY + stampH / 2);
  ctx.rotate((layout.stampRotationDeg * Math.PI) / 180);
  ctx.translate(-stampW / 2, -stampH / 2);

  ctx.drawImage(bgImg, 0, 0, stampW, stampH);

  const palW = pct(layout.hackpal.w, stampW);
  const palH = palW * (palImg.naturalHeight / palImg.naturalWidth);
  ctx.drawImage(
    palImg,
    pct(layout.hackpal.cx, stampW) - palW / 2,
    pct(layout.hackpal.bottom, stampH) - palH,
    palW,
    palH
  );

  if (accessoryImg) {
    const accW = pct(layout.accessory.w, stampW);
    const accH =
      accW * (accessoryImg.naturalHeight / accessoryImg.naturalWidth);
    const accCx = pct(layout.accessory.cx, stampW);
    const accBottom = pct(layout.accessory.bottom, stampH);
    const accCy = accBottom - accH / 2;

    ctx.save();
    ctx.translate(accCx, accCy);
    if (layout.accessory.rotationDeg !== 0) {
      ctx.rotate((layout.accessory.rotationDeg * Math.PI) / 180);
    }
    ctx.drawImage(accessoryImg, -accW / 2, -accH / 2, accW, accH);
    ctx.restore();
  }

  if (effectImg) {
    const effW = pct(layout.effects.w, stampW);
    const effH = effW * (effectImg.naturalHeight / effectImg.naturalWidth);
    ctx.drawImage(
      effectImg,
      pct(layout.effects.x, stampW),
      pct(layout.effects.y, stampH),
      effW,
      effH
    );
  }

  drawText(
    ctx,
    HEADER_TEXT_LINES[customization.headerText],
    layout.headerText,
    HEADER_TEXT_GRADIENT[backdrop],
    stampW,
    stampH
  );
  if (signText) {
    const signLayout = resolveSignTextLayout(signText, layout.signText, stampW);
    drawText(
      ctx,
      signLayout.lines,
      {
        ...layout.signText,
        fontPct: signLayout.fontPct,
        y: signLayout.y,
        maxLines: signLayout.lines.length,
      },
      SIGN_TEXT_GRADIENT,
      stampW,
      stampH
    );
  }

  ctx.restore();

  const waveW = pct(layout.wave.w, canvas.width);
  const waveH = waveW * (waveImg.naturalHeight / waveImg.naturalWidth);
  ctx.drawImage(
    waveImg,
    pct(layout.wave.x, canvas.width),
    pct(layout.wave.y, canvas.height),
    waveW,
    waveH
  );

  drawInkStamp(ctx, inkStampImg, stampName, palette.ink, layout);

  return canvas;
};

export const downloadGraphic = async (
  customization: GraphicCustomization,
  firstName: string
): Promise<void> => {
  const canvas = await renderGraphicToCanvas(customization, firstName);

  await new Promise<void>((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) {
        reject(new Error("Failed to export graphic"));
        return;
      }
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = "HackTheNorth2026.png";
      anchor.click();
      URL.revokeObjectURL(url);
      resolve();
    }, "image/png");
  });
};
